// "Want this next day? Order within 2 hours, 14 minutes" on the PDP. The
// dispatch cut-off is a UK business rule, so the day and time are read in
// Europe/London rather than the visitor's clock. Windows (from the old theme,
// with a 13:00 cut-off):
//   NEXT_DAY:       Mon 13:00 → Thu 13:00, counting to the next daily cut-off
//   FRIDAY_CUT_OFF: Thu 13:00 → Fri 12:00, the Friday cut-off is an hour early
//   MONDAY_CUT_OFF: Fri 12:00 → Mon 13:00, counting to Monday's cut-off
// The block hides when today or the next working day is an England & Wales
// bank holiday; if that lookup fails the countdown shows.

import { CLASSES, DELIVERY_COUNTDOWN } from '../../utils/constants';
import toggleVisibility from '../../utils/toggleVisibility';
import safeJSONParse from '../../utils/safeJsonParse';

const { DAY, WINDOW, WEEKDAYS, NEXT_WORKING_DAY_OFFSET } = DELIVERY_COUNTDOWN;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const ISO_DATE_LENGTH = 10;
const HOUR_SECONDS = SECONDS_PER_MINUTE * MINUTES_PER_HOUR;
const DAY_SECONDS = HOURS_PER_DAY * HOUR_SECONDS;

const londonFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: DELIVERY_COUNTDOWN.TIME_ZONE,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23'
});

const getLondonNow = () => {
  const parts = Object.fromEntries(
    londonFormatter.formatToParts(new Date()).map((part) => [part.type, part.value])
  );
  return {
    weekday: WEEKDAYS.indexOf(parts.weekday),
    secondsOfDay: Number(parts.hour) * HOUR_SECONDS + Number(parts.minute) * SECONDS_PER_MINUTE + Number(parts.second),
    isoDate: `${parts.year}-${parts.month}-${parts.day}`
  };
};

const getDeliveryWindow = ({ weekday, secondsOfDay }, cutOffSeconds) => {
  const fridayCutOffSeconds = cutOffSeconds - HOUR_SECONDS;
  const isWeekend = weekday === DAY.SATURDAY || weekday === DAY.SUNDAY;
  const isAfterFridayCutOff = weekday === DAY.FRIDAY && secondsOfDay >= fridayCutOffSeconds;
  const isBeforeMondayCutOff = weekday === DAY.MONDAY && secondsOfDay < cutOffSeconds;
  if (isWeekend || isAfterFridayCutOff || isBeforeMondayCutOff) return WINDOW.MONDAY_CUT_OFF;

  const isAfterThursdayCutOff = weekday === DAY.THURSDAY && secondsOfDay >= cutOffSeconds;
  const isBeforeFridayCutOff = weekday === DAY.FRIDAY && secondsOfDay < fridayCutOffSeconds;
  if (isAfterThursdayCutOff || isBeforeFridayCutOff) return WINDOW.FRIDAY_CUT_OFF;

  return WINDOW.NEXT_DAY;
};

// Seconds until the cut-off that closes the current window
const getSecondsRemaining = ({ weekday, secondsOfDay }, deliveryWindow, cutOffSeconds) => {
  let targetSeconds = cutOffSeconds;
  let daysAhead = 0;

  switch (deliveryWindow) {
    case WINDOW.FRIDAY_CUT_OFF:
      targetSeconds = cutOffSeconds - HOUR_SECONDS;
      daysAhead = weekday === DAY.THURSDAY ? 1 : 0;
      break;
    case WINDOW.MONDAY_CUT_OFF:
      daysAhead = (DAY.MONDAY - weekday + DAYS_PER_WEEK) % DAYS_PER_WEEK;
      break;
    default:
      daysAhead = secondsOfDay >= cutOffSeconds ? 1 : 0;
  }

  return daysAhead * DAY_SECONDS + targetSeconds - secondsOfDay;
};

const formatUnit = (value, singular, plural) => `${value} ${value === 1 ? singular : plural}`;

const formatRemaining = (totalSeconds, isShowingSeconds) => {
  const { strings } = window.theme;
  const hours = Math.floor(totalSeconds / HOUR_SECONDS);
  const minutes = Math.floor((totalSeconds % HOUR_SECONDS) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  const parts = [
    formatUnit(hours, strings.countdownHour, strings.countdownHours),
    formatUnit(minutes, strings.countdownMinute, strings.countdownMinutes)
  ];
  if (isShowingSeconds) parts.push(formatUnit(seconds, strings.countdownSecond, strings.countdownSeconds));
  return parts.join(', ');
};

const addDays = (isoDate, days) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, ISO_DATE_LENGTH);
};

// sessionStorage access throws under blocked-cookies settings, so a failed
// read or write just means a fresh fetch per page
const getBankHolidayDates = async () => {
  try {
    const cachedDates = safeJSONParse(sessionStorage.getItem(DELIVERY_COUNTDOWN.STORAGE_KEY), null);
    // Anything but an array (another script's value, a bad write) is refetched
    if (Array.isArray(cachedDates)) return cachedDates;
  } catch {
    // fall through to the fetch
  }

  // Bounded so a hanging request can't keep the block hidden; a timeout
  // rejects and takes the same fail-open path as any other error
  const response = await fetch(DELIVERY_COUNTDOWN.BANK_HOLIDAYS_URL, {
    signal: AbortSignal.timeout(DELIVERY_COUNTDOWN.FETCH_TIMEOUT_MS)
  });
  if (!response.ok) throw new Error(`Bank holidays request failed: ${response.status}`);
  const data = await response.json();
  const dates = (data[DELIVERY_COUNTDOWN.BANK_HOLIDAYS_REGION]?.events || []).map((event) => event.date);

  try {
    sessionStorage.setItem(DELIVERY_COUNTDOWN.STORAGE_KEY, JSON.stringify(dates));
  } catch {
    // storage blocked — fetched on the next page instead
  }
  return dates;
};

// Dispatch is blocked when today or the next working day is a bank holiday
const isDispatchBlockedByBankHoliday = async ({ weekday, isoDate }) => {
  try {
    const dates = await getBankHolidayDates();
    const nextWorkingDayOffset = NEXT_WORKING_DAY_OFFSET[weekday] ?? NEXT_WORKING_DAY_OFFSET.DEFAULT;
    return dates.includes(isoDate) || dates.includes(addDays(isoDate, nextWorkingDayOffset));
  } catch (error) {
    console.error('Bank holiday check failed, showing the delivery countdown:', error);
    return false;
  }
};

// Returns false when the window has no message (the merchant cleared it), so
// the block is hidden rather than showing a bare duration
const showMessage = (countdownEl, deliveryWindow) => {
  let hasMessage = false;
  countdownEl.querySelectorAll('[data-delivery-countdown-message]').forEach((messageEl) => {
    const isActive = Number(messageEl.dataset.deliveryCountdownMessage) === deliveryWindow;
    messageEl.hidden = !isActive;
    if (isActive) hasMessage = true;
  });
  return hasMessage;
};

const updateCountdown = (countdownEl, state) => {
  const londonNow = getLondonNow();
  const deliveryWindow = getDeliveryWindow(londonNow, state.cutOffSeconds);
  if (deliveryWindow !== state.deliveryWindow) {
    state.deliveryWindow = deliveryWindow;
    // The block reserves its space from first paint (invisible, not hidden),
    // so revealing it causes no layout shift. A window with no message (or a
    // bank holiday, below) collapses it; a later window with a message must be
    // able to bring it back, so the reveal undoes the collapse too.
    const hasMessage = showMessage(countdownEl, deliveryWindow);
    toggleVisibility(countdownEl, hasMessage);
    if (hasMessage) countdownEl.classList.remove(CLASSES.INVISIBLE);
  }
  const secondsRemaining = getSecondsRemaining(londonNow, deliveryWindow, state.cutOffSeconds);
  state.timeEl.textContent = formatRemaining(secondsRemaining, state.isShowingSeconds);
};

const startTicking = (countdownEl, state) => {
  updateCountdown(countdownEl, state);
  const intervalId = setInterval(() => {
    // The section can be re-rendered underneath us in the theme editor
    if (!countdownEl.isConnected) {
      clearInterval(intervalId);
      return;
    }
    updateCountdown(countdownEl, state);
  }, DELIVERY_COUNTDOWN.TICK_MS);
};

const initDeliveryCountdown = async () => {
  const countdownEl = document.querySelector('[data-delivery-countdown]');
  if (!countdownEl || countdownEl.dataset.deliveryCountdownInit) return;
  countdownEl.dataset.deliveryCountdownInit = 'true';

  const timeEl = countdownEl.querySelector('[data-delivery-countdown-time]');
  if (!timeEl) return;

  const { deliveryCountdownHour, deliveryCountdownMinute } = countdownEl.dataset;
  const cutOffHourSeconds = Number(deliveryCountdownHour) * HOUR_SECONDS;
  const cutOffSeconds = cutOffHourSeconds + Number(deliveryCountdownMinute) * SECONDS_PER_MINUTE;
  if (Number.isNaN(cutOffSeconds)) return;

  const state = {
    timeEl,
    cutOffSeconds,
    isShowingSeconds: 'deliveryCountdownSeconds' in countdownEl.dataset,
    deliveryWindow: null
  };

  if (await isDispatchBlockedByBankHoliday(getLondonNow())) {
    toggleVisibility(countdownEl, false);
    return;
  }
  startTicking(countdownEl, state);
};

export default initDeliveryCountdown;
